import { useEffect, useState, useCallback, useRef } from 'react';

export interface InactivityTimerOptions {
  isActive: boolean;
  onInactivityLimitReached?: () => void;
  warningThreshold?: number;
  inactivityTimeout?: number; // New parameter for configurable timeout
  initialDelay?: number;
  visualBreathingRoom?: number;
}

export const useInactivityTimer = ({
  isActive,
  onInactivityLimitReached,
  warningThreshold = 4,
  inactivityTimeout = 6, // Default to 6 seconds
  initialDelay = 1000, // Default delay of 1 second
  visualBreathingRoom = 250 // Default 250ms visual breathing room
}: InactivityTimerOptions) => {
  const [millisecondsIdle, setMillisecondsIdle] = useState<number>(0);
  const [isWarning, setIsWarning] = useState<boolean>(false);
  const [isInitialDelayComplete, setIsInitialDelayComplete] = useState<boolean>(false);
  const [isCountingDown, setIsCountingDown] = useState<boolean>(true);
  const initialDelayTimer = useRef<NodeJS.Timeout | null>(null);
  const breathingRoomTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Convert to milliseconds for smoother progress
  const WARNING_THRESHOLD_MS = warningThreshold * 1000; // Milliseconds before warning
  const IDLE_LIMIT_MS = inactivityTimeout * 1000; // Milliseconds before considered idle
  const UPDATE_INTERVAL = 16; // Update every 16ms for smooth progress (60fps)

  // Reset inactivity counter and initial delay
  const resetInactivity = useCallback(() => {
    setMillisecondsIdle(0);
    setIsWarning(false);
    setIsCountingDown(false); // Stop counting down immediately when user interacts
    
    // Clear any existing timers
    if (initialDelayTimer.current) {
      clearTimeout(initialDelayTimer.current);
    }
    
    if (breathingRoomTimer.current) {
      clearTimeout(breathingRoomTimer.current);
    }
    
    // Set up the breathing room timer to provide visual pause
    breathingRoomTimer.current = setTimeout(() => {
      setIsCountingDown(true); // Start counting down after the breathing room period
    }, visualBreathingRoom);
    
    // Only reset the initial delay if it hasn't completed yet
    if (!isInitialDelayComplete) {
      initialDelayTimer.current = setTimeout(() => {
        setIsInitialDelayComplete(true);
      }, initialDelay);
    }
    
  }, [initialDelay, visualBreathingRoom, isInitialDelayComplete]);

  // Start inactivity timer
  useEffect(() => {
    if (!isActive) {
      resetInactivity();
      return;
    }

    // Set up the initial delay when session becomes active
    if (!isInitialDelayComplete) {
      initialDelayTimer.current = setTimeout(() => {
        setIsInitialDelayComplete(true);
      }, initialDelay);
      
      return () => {
        if (initialDelayTimer.current) {
          clearTimeout(initialDelayTimer.current);
        }
      };
    }
    
    // Only start incrementing the inactivity timer after initial delay
    if (!isInitialDelayComplete || !isCountingDown) {
      return;
    }

    const timer = setInterval(() => {
      setMillisecondsIdle(prev => {
        const newValue = prev + UPDATE_INTERVAL;
        
        // Set warning if approaching limit
        if (newValue >= WARNING_THRESHOLD_MS && newValue < IDLE_LIMIT_MS) {
          setIsWarning(true);
        }
        
        // Call the callback when we reach the idle limit
        if (newValue >= IDLE_LIMIT_MS && onInactivityLimitReached) {
          onInactivityLimitReached();
          return IDLE_LIMIT_MS; // Keep it at the limit
        }
        
        return newValue;
      });
    }, UPDATE_INTERVAL);

    return () => clearInterval(timer);
  }, [isActive, isInitialDelayComplete, isCountingDown, IDLE_LIMIT_MS, WARNING_THRESHOLD_MS, initialDelay, onInactivityLimitReached, resetInactivity]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (initialDelayTimer.current) {
        clearTimeout(initialDelayTimer.current);
      }
      if (breathingRoomTimer.current) {
        clearTimeout(breathingRoomTimer.current);
      }
    };
  }, []);

  // Calculate progress as a percentage (0-100)
  const inactivityProgress = Math.min(100, (millisecondsIdle / IDLE_LIMIT_MS) * 100);
  
  // Convert milliseconds to seconds for backward compatibility
  const secondsIdle = Math.floor(millisecondsIdle / 1000);

  return {
    secondsIdle,
    inactivityProgress,
    resetInactivity,
    isWarning,
    isInitialDelayComplete,
    isCountingDown,
    isInactivityWarning: isWarning // For backward compatibility
  };
};

export default useInactivityTimer; 